import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Tabs, Tab, Table, Button, Dialog, TextField, Snackbar, Select, MenuItem } from '@mui/material';

const AdminMergeComponent = () => {
  const [activeTab, setActiveTab] = useState('degrees');
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [primaryRecord, setPrimaryRecord] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form, setForm] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    fetchCollectionData(activeTab);
  }, [activeTab]);

  const fetchCollectionData = (collection) => {
    axios.get(`/api/${collection}`)
      .then(response => {
        setData(response.data);
        setFilteredData(response.data);
      })
      .catch(error => console.error('Error fetching data:', error));
  };

  const handleSearch = (value) => {
    if (!value) {
      setFilteredData(data);
      return;
    }
    const lowercasedValue = value.toLowerCase();
    const filtered = data.filter(item =>
      Object.keys(item).some(key =>
        item[key] && item[key].toString().toLowerCase().includes(lowercasedValue)
      )
    );
    setFilteredData(filtered);
  };

  const handleSync = () => {
    axios.post(`/api/sync/${activeTab}`)
      .then(() => {
        setSnackbarMessage('Synchronization successful');
        setSnackbarOpen(true);
        fetchCollectionData(activeTab);
      })
      .catch(error => {
        console.error('Error syncing data:', error);
        setSnackbarMessage(`Synchronization failed: ${error.message}`);
        setSnackbarOpen(true);
      });
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setForm(record);
    setIsModalVisible(true);
  };

  const handleSave = () => {
    const method = editingRecord ? axios.put : axios.post;
    const url = editingRecord ? `/api/${activeTab}/${editingRecord._id}` : `/api/${activeTab}`;
    method(url, form)
      .then(() => {
        setSnackbarMessage('Operation successful');
        setSnackbarOpen(true);
        setIsModalVisible(false);
        setEditingRecord(null);
        fetchCollectionData(activeTab);
      })
      .catch(error => {
        console.error('Error saving data:', error);
        setSnackbarMessage(`Operation failed: ${error.message}`);
        setSnackbarOpen(true);
      });
  };

  const handleDelete = (recordId) => {
    axios.delete(`/api/${activeTab}/${recordId}`)
      .then(() => {
        setSnackbarMessage('Deletion successful');
        setSnackbarOpen(true);
        fetchCollectionData(activeTab);
      })
      .catch(error => {
        console.error('Error deleting record:', error);
        setSnackbarMessage(`Deletion failed: ${error.message}`);
        setSnackbarOpen(true);
      });
  };

  const handleMerge = () => {
    const mergeIds = selectedRowKeys.filter(key => key !== primaryRecord);
    axios.post(`/api/${activeTab}/merge`, { primaryId: primaryRecord, mergeIds })
      .then(() => {
        setSnackbarMessage('Merge successful');
        setSnackbarOpen(true);
        setSelectedRowKeys([]);
        setPrimaryRecord(null);
        fetchCollectionData(activeTab);
      })
      .catch(error => {
        console.error('Error merging records:', error);
        setSnackbarMessage(`Merge failed: ${error.message}`);
        setSnackbarOpen(true);
      });
  };

  // Dynamically generate columns from data keys
  const columns = Object.keys(data[0] || {}).filter(key => key !== '_id').map(key => ({
    title: key.charAt(0).toUpperCase() + key.slice(1),
    field: key,
    sorting: true,
    filtering: true,
  }));

  // Add the actions column
  columns.push({
    title: 'Actions',
    field: 'actions',
    render: (rowData) => (
      <>
        <Button onClick={() => handleEdit(rowData)} style={{ marginRight: 8 }}>Edit</Button>
        <Button onClick={() => handleDelete(rowData._id)} color="secondary">Delete</Button>
      </>
    )
  });

  return (
    <div>
      <Tabs value={activeTab} onChange={(event, newValue) => setActiveTab(newValue)}>
        {['degrees', 'institutions', 'departments', 'designations', 'organizations', 'skills', 'locations'].map(tab => (
          <Tab label={tab.charAt(0).toUpperCase() + tab.slice(1)} value={tab} key={tab} />
        ))}
      </Tabs>
      <Button onClick={() => setIsModalVisible(true)} variant="contained" style={{ marginBottom: 16 }}>Add New</Button>
      <Button onClick={handleSync} variant="contained" style={{ marginBottom: 16, marginLeft: 8 }}>Sync Data</Button>
      <TextField placeholder="Search..." onChange={e => handleSearch(e.target.value)} style={{ width: 200, marginBottom: 16 }} />
      <Select
        style={{ width: 200, marginBottom: 16 }}
        displayEmpty
        onChange={e => setPrimaryRecord(e.target.value)}
        value={primaryRecord}
      >
        <MenuItem value="" disabled>Select Primary Record</MenuItem>
        {filteredData.filter(item => selectedRowKeys.includes(item._id)).map(item => (
          <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>
        ))}
      </Select>
      <Button onClick={handleMerge} variant="contained" color="primary" disabled={!primaryRecord || selectedRowKeys.length <= 1}>Merge Selected</Button>
      <Table
        columns={columns}
        data={filteredData}
        options={{
          search: false,
          sorting: true,
          filtering: true,
          paging: false,
          actionsColumnIndex: -1,
        }}
        localization={{
          header: {
            actions: 'Actions',
          },
        }}
      />
      <Dialog
        open={isModalVisible}
        onClose={() => {
          setIsModalVisible(false);
          setEditingRecord(null);  // Clear editing record when closing modal
        }}
      >
        <div style={{ padding: 20 }}>
          <h2>{editingRecord ? "Edit Record" : "Add Record"}</h2>
          {columns.filter(col => col.field !== 'actions').map(col => (
            <TextField
              label={col.title}
              value={form[col.field] || ''}
              onChange={e => setForm({ ...form, [col.field]: e.target.value })}
              required
              fullWidth
              margin="normal"
              key={col.field}
            />
          ))}
          <Button onClick={handleSave} variant="contained" color="primary" style={{ marginTop: 16 }}>
            {editingRecord ? "Save Changes" : "Add Record"}
          </Button>
        </div>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        autoHideDuration={6000}
      />
    </div>
  );
};

export default AdminMergeComponent;
